import { useState, useRef } from "react";
import { extractFormValues } from "@components";
import { htmlToPlainText } from "@utils";

export function useEditorState({
  reset,
  getValues,
  setValue,
  formValues,
  watchedInvoices,
  watchedMandatory,
  watchedPdf,
  watchedCardPayment,
  watchedPaymentProfile,
}) {
  /* ----------------------- state ----------------------- */
  const [step, setStep] = useState("select");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("email");
  const [previewMode, setPreviewMode] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  const originalRef = useRef(null);
  const editSnapshotRef = useRef(null);

  /* ----------------------- dirty flags ----------------- */
  const orig = originalRef.current;

  const invoiceEdited =
    orig != null &&
    (watchedInvoices !== orig.invoicesRequested ||
      watchedMandatory !== orig.mandatory);

  const pdfEdited = orig != null && watchedPdf !== orig.attachPdfStatement;

  const cardEdited = orig != null && watchedCardPayment !== orig.allowCardPayment;

  const paymentProfileEdited =
    orig != null && (watchedPaymentProfile ?? null) !== (orig.paymentProfileId ?? null);

  const channelEdited =
    orig != null &&
    (emailEnabled !== orig.emailEnabled || smsEnabled !== orig.smsEnabled);

  const excludeKeys = ["customAmount"];

  const differsFrom = (refValues) => {
    if (!refValues) return false;
    for (const key of Object.keys(formValues)) {
      if (excludeKeys.includes(key)) continue;
      if (formValues[key] !== refValues[key]) return true;
    }
    return false;
  };

  const isEdited =
    orig != null && (differsFrom(orig.values) || channelEdited);

  /* ---- edit-session dirty (differs from snapshot taken on edit entry) ---- */
  const hasUnappliedChanges = (() => {
    if (previewMode || !editSnapshotRef.current) return false;
    const snap = editSnapshotRef.current;
    if (emailEnabled !== snap.emailEnabled || smsEnabled !== snap.smsEnabled)
      return true;
    return differsFrom(snap.values);
  })();

  /* ----------------------- handlers -------------------- */
  const setOriginalFromTemplate = (template, hasEmail, hasSms, formVals) => {
    originalRef.current = {
      values: formVals,
      invoicesRequested: template.invoicesRequested ?? "Custom",
      mandatory: template.paymentRequirement === "Mandatory",
      attachPdfStatement: template.attachPdfStatement ?? false,
      allowCardPayment: template.allowCardPayment ?? false,
      paymentProfileId: template.paymentProfileId ?? null,
      emailEnabled: hasEmail,
      smsEnabled: hasSms,
    };
  };

  const initFromTemplate = (template, extraValues) => {
    if (!template) return;
    setSelectedTemplate(template);

    const hasEmail =
      template.messages?.some((m) => m.communicationMethod === "Email") ??
      false;
    const hasSms =
      template.messages?.some((m) => m.communicationMethod === "Sms") ?? false;

    const formVals = { ...extractFormValues(template), ...(extraValues ?? {}) };
    reset(formVals);
    setEmailEnabled(hasEmail);
    setSmsEnabled(hasSms);
    setActiveTab(hasEmail ? "email" : "sms");
    setPreviewMode(true);
    setStep("preview");
    setOriginalFromTemplate(template, hasEmail, hasSms, formVals);
  };

  const handleToggleChannel = (channel) => {
    if (channel === "email") {
      const next = !emailEnabled;
      setEmailEnabled(next);
      if (next && !getValues("emailMainText")) {
        setValue("emailMainText", getValues("smsText") || "");
      }
      if (!next && activeTab === "email") {
        setActiveTab(smsEnabled ? "sms" : "email");
      }
    } else {
      const next = !smsEnabled;
      setSmsEnabled(next);
      if (next && !getValues("smsText")) {
        setValue("smsText", htmlToPlainText(getValues("emailMainText")) || "");
      }
      if (!next && activeTab === "sms") {
        setActiveTab(emailEnabled ? "email" : "sms");
      }
    }
  };

  const handleBackToSelect = () => {
    setStep("select");
    setSelectedTemplate(null);
    setPreviewMode(true);
    originalRef.current = null;
  };

  const resetToOriginalTemplate = () => {
    if (!selectedTemplate || !orig) return;
    reset(extractFormValues(selectedTemplate));
    setEmailEnabled(orig.emailEnabled);
    setSmsEnabled(orig.smsEnabled);
    setActiveTab(orig.emailEnabled ? "email" : "sms");
  };

  const handleResetEdits = () => {
    resetToOriginalTemplate();
    setPreviewMode(true);
  };

  const handleResetInEditMode = () => {
    resetToOriginalTemplate();
  };

  const handleEnterEdit = () => {
    editSnapshotRef.current = {
      values: getValues(),
      emailEnabled,
      smsEnabled,
      activeTab,
    };
    setPreviewMode(false);
  };

  const handleCancelEdit = () => {
    if (editSnapshotRef.current) {
      reset(editSnapshotRef.current.values);
      setEmailEnabled(editSnapshotRef.current.emailEnabled);
      setSmsEnabled(editSnapshotRef.current.smsEnabled);
      setActiveTab(editSnapshotRef.current.activeTab);
    }
    setPreviewMode(true);
    editSnapshotRef.current = null;
  };

  const handleDoneEdit = () => {
    setPreviewMode(true);
    editSnapshotRef.current = null;
  };

  const applyNewTemplate = (newTemplate) => {
    setSelectedTemplate(newTemplate);
    const formVals = extractFormValues(newTemplate);
    reset(formVals);

    const hasEmail =
      newTemplate.messages?.some((m) => m.communicationMethod === "Email") ??
      false;
    const hasSms =
      newTemplate.messages?.some((m) => m.communicationMethod === "Sms") ??
      false;

    setEmailEnabled(hasEmail);
    setSmsEnabled(hasSms);
    setActiveTab(hasEmail ? "email" : "sms");
    setPreviewMode(true);
    editSnapshotRef.current = null;
    setOriginalFromTemplate(newTemplate, hasEmail, hasSms, formVals);
  };

  const resetEditorState = () => {
    setStep("select");
    setSelectedTemplate(null);
    setActiveTab("email");
    setPreviewMode(true);
    setEmailEnabled(false);
    setSmsEnabled(false);
    originalRef.current = null;
    editSnapshotRef.current = null;
  };

  return {
    step,
    setStep,
    selectedTemplate,
    activeTab,
    setActiveTab,
    previewMode,
    emailEnabled,
    smsEnabled,
    invoiceEdited,
    pdfEdited,
    cardEdited,
    paymentProfileEdited,
    channelEdited,
    isEdited,
    hasUnappliedChanges,
    initFromTemplate,
    handleToggleChannel,
    handleBackToSelect,
    handleResetEdits,
    handleResetInEditMode,
    handleEnterEdit,
    handleCancelEdit,
    handleDoneEdit,
    applyNewTemplate,
    resetEditorState,
  };
}
